import { MdCancel } from '@react-icons/all-files/md/MdCancel';
import { MdCheckCircle } from '@react-icons/all-files/md/MdCheckCircle';
import { MdRadioButtonUnchecked } from '@react-icons/all-files/md/MdRadioButtonUnchecked';
import classNames from 'classnames';

import { Slideout, SlideoutCommonProps } from 'components/design-system/Slideout';
import { CancellationReason, OrderStatus } from 'consts/order_statuses';
import { PaymentMethods } from 'consts/payment_methods';
import { Order } from 'types';

type StatusDetailsModalProps = {
  order: Order;
} & SlideoutCommonProps;

type StatusBlobProps = {
  order: Order;
  renderedStatus: OrderStatus;
  previousStepStatus: StepStatus;
  currentStepStatus: StepStatus;
  nextStepStatus: StepStatus;
};

const statusLabels = {
  [OrderStatus.AWAITING_PAYMENT]: {
    incomplete: {
      label: 'BUYER HAS NOT PAID',
      description: "We're waiting on the buyer to transfer funds."
    },
    complete: {
      label: 'BUYER PAID',
      description: 'The buyer has paid for the watch.'
    }
  },
  [OrderStatus.PROCESSING]: {
    incomplete: {
      label: 'ORDER NOT ACCEPTED',
      description: 'You have 24 hours to accept this order.'
    },
    complete: {
      label: 'ORDER ACCEPTED',
      description: 'Your watch has been ordered and you have accepted the sale.'
    }
  },
  [OrderStatus.AWAITING_CONFIRMATION]: {
    incomplete: {
      label: 'ORDER NOT ACCEPTED',
      description: 'You have 24 hours to accept this order.'
    },
    complete: {
      label: 'ORDER ACCEPTED',
      description: 'Your watch has been ordered and you have accepted the sale.'
    }
  },
  [OrderStatus.AWAITING_SHIPMENT]: {
    incomplete: {
      label: 'PAYMENT NOT SENT',
      description:
        "Once we authenticate the watch we'll release the proceeds from this sale. It should be available for transfer within 2 business days of release."
    },
    complete: {
      label: 'PAYMENT SENT',
      description:
        'The proceeds from this sale have been transferred to your Bezel wallet. They should be available in 2 business days.'
    }
  },
  [OrderStatus.AWAITING_RECEIPT]: {
    incomplete: {
      label: 'NOT DELIVERED TO BEZEL YET',
      description: 'The watch has not been delivered to Bezel HQ yet.'
    },
    complete: {
      label: 'DELIVERED TO BEZEL',
      description: "We've received the watch and have started the authentication process."
    }
  },
  [OrderStatus.AWAITING_AUTH]: {
    incomplete: {
      label: 'NOT AUTHENTICATED YET',
      description: 'Once we receive the watch our team of experts will thoroughly authenticate it.'
    },
    complete: {
      label: 'AUTHENTICATED',
      description: 'The watch has been fully authenticated by our team. Thank you for sending us a great watch!'
    }
  },
  [OrderStatus.AWAITING_DELIVERY]: {
    incomplete: {
      label: 'PAYMENT NOT SENT',
      description:
        "Once we authenticate the watch we'll release the proceeds from this sale. It should be available for transfer within 2 business days of release."
    },
    complete: {
      label: 'PAYMENT SENT',
      description:
        'The proceeds from this sale have been transferred to your Bezel wallet. They should be available in 2 business days.'
    }
  },
  [OrderStatus.DELIVERED]: {
    incomplete: {
      label: 'PAYMENT NOT SENT',
      description:
        "Once we authenticate the watch we'll release the proceeds from this sale. It should be available for transfer within 2 business days of release."
    },
    complete: {
      label: 'PAYMENT SENT',
      description:
        'The proceeds from this sale have been transferred to your Bezel wallet. They should be available in 2 business days.'
    }
  },
  // Should never be used
  [OrderStatus.CANCELLED]: {
    incomplete: {
      label: 'ORDER CANCELLED',
      description: 'This order has been cancelled.'
    },
    complete: {
      label: 'ORDER CANCELLED',
      description: 'This order has been cancelled.'
    }
  }
};

const wireOrderSequence = [
  OrderStatus.AWAITING_CONFIRMATION,
  OrderStatus.AWAITING_PAYMENT,
  OrderStatus.AWAITING_RECEIPT,
  OrderStatus.AWAITING_AUTH,
  OrderStatus.AWAITING_SHIPMENT,
  OrderStatus.AWAITING_DELIVERY,
  OrderStatus.DELIVERED
];

const orderSequence = [
  OrderStatus.AWAITING_CONFIRMATION,
  OrderStatus.AWAITING_RECEIPT,
  OrderStatus.AWAITING_AUTH,
  OrderStatus.AWAITING_SHIPMENT,
  OrderStatus.AWAITING_DELIVERY,
  OrderStatus.DELIVERED
];

const buyerNeverPaidOrderSequence = [OrderStatus.AWAITING_CONFIRMATION, OrderStatus.CANCELLED];

const failedAuthOrderSequence = [
  OrderStatus.AWAITING_CONFIRMATION,
  OrderStatus.AWAITING_RECEIPT,
  OrderStatus.CANCELLED
];

const orderCancelledLabels = {
  [CancellationReason.BUYER_CANCELLED]: {
    sequence: [OrderStatus.CANCELLED],
    label: 'BUYER CANCELLED',
    description: 'Buyer cancelled the order.'
  },
  [CancellationReason.BUYER_NEVER_PAID]: {
    sequence: buyerNeverPaidOrderSequence,
    label: 'PAYMENT NEVER RECEIVED',
    description: 'We never received the funds for your sale.'
  },
  [CancellationReason.SELLER_DECLINED]: {
    sequence: [OrderStatus.CANCELLED],
    label: 'SALE DECLINED',
    description: "You've declined this sale."
  },
  [CancellationReason.FAILED_AUTH]: {
    sequence: failedAuthOrderSequence,
    label: 'AUTHENTICATION FAILED',
    description: 'The watch we received was not up to the Bezel standard of authenticity.'
  },
  [CancellationReason.OTHER]: {
    sequence: [OrderStatus.CANCELLED],
    label: 'ORDER CANCELLED',
    description: 'This order has been cancelled.'
  }
};

const completeStatusStyle = {
  border: '1px solid rgba(7, 116, 91, 0.6)',
  backgroundColor: 'rgba(7, 116, 91, 0.02)',
  color: 'rgb(7, 116, 91)'
};

const incompleteStatusStyle = {
  border: '1px solid rgba(26, 42, 50, 0.06)',
  backgroundColor: 'rgba(26, 42, 50, 0.02)',
  color: 'rgb(26, 42, 50)'
};

const cancelledStatusStyle = {
  border: '1px solid rgba(241, 94, 94, 0.6)',
  backgroundColor: 'rgba(241, 94, 94, 0.02)',
  color: 'rgb(241, 94, 94)'
};

const currentStepBorderStyle = {
  background: 'linear-gradient(rgb(7, 116, 91) 50%, #d4d4d4 50%) top left',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1px'
};

const currentStepBorderStyleWithNextStepCancelled = {
  background: 'linear-gradient(rgb(7, 116, 91) 50%, rgb(241, 94, 94) 50%) top left',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1px'
};

const completeBorderStyle = {
  borderLeft: '1px solid rgb(7, 116, 91)'
};

const incompleteBorderStyle = {
  borderLeft: '1px solid #d4d4d4'
};

const cancelledBorderStyle = {
  borderLeft: '1px solid rgb(241, 94, 94)'
};

const completeIconStyle = {
  color: 'rgb(7, 116, 91)'
};

const incompleteIconStyle = {
  color: '#d4d4d4'
};

const cancelledIconStyle = {
  color: 'rgb(241, 94, 94)'
};

enum StepStatus {
  COMPLETE = 'COMPLETE',
  INCOMPLETE = 'INCOMPLETE',
  CANCELLED = 'CANCELLED'
}

const StatusBlob: React.FC<StatusBlobProps> = ({
  order,
  renderedStatus,
  previousStepStatus,
  currentStepStatus,
  nextStepStatus
}) => {
  const previousStepComplete = previousStepStatus === StepStatus.COMPLETE;
  const nextStepComplete = nextStepStatus === StepStatus.COMPLETE;
  const currentStepComplete = currentStepStatus === StepStatus.COMPLETE;
  const nextStepCancelled = nextStepStatus === StepStatus.CANCELLED;
  const currentStepCancelled = currentStepStatus === StepStatus.CANCELLED;

  let { label, description } = statusLabels[renderedStatus][currentStepComplete ? 'complete' : 'incomplete'];

  if (order.status === OrderStatus.CANCELLED) {
    const cancellationReason = order.cancellationReason || CancellationReason.OTHER;

    const { label: cancelledLabel, description: cancelledDescription } = orderCancelledLabels[cancellationReason];

    label = cancelledLabel;
    description = cancelledDescription;
  }

  let currentStepStyle: React.CSSProperties;
  let currentBorderStyle: React.CSSProperties;
  let currentIcon: React.ReactNode;

  if (currentStepCancelled) {
    currentIcon = <MdCancel className='bg-light' style={cancelledIconStyle} size={17} />;
    currentStepStyle = cancelledStatusStyle;
    currentBorderStyle = cancelledBorderStyle;
  } else if (currentStepComplete && nextStepCancelled) {
    currentIcon = <MdCheckCircle className='bg-light' style={completeIconStyle} size={17} />;
    currentStepStyle = completeStatusStyle;
    currentBorderStyle = currentStepBorderStyleWithNextStepCancelled;
  } else if (previousStepComplete && currentStepComplete && nextStepComplete) {
    currentIcon = <MdCheckCircle className='bg-light' style={completeIconStyle} size={17} />;
    currentBorderStyle = completeBorderStyle;
    currentStepStyle = completeStatusStyle;
  } else if (previousStepComplete && currentStepComplete && !nextStepComplete) {
    currentIcon = <MdCheckCircle className='bg-light' style={completeIconStyle} size={17} />;
    currentBorderStyle = currentStepBorderStyle;
    currentStepStyle = completeStatusStyle;
  } else {
    currentIcon = <MdRadioButtonUnchecked className='bg-light' style={incompleteIconStyle} size={17} />;
    currentBorderStyle = incompleteBorderStyle;
    currentStepStyle = incompleteStatusStyle;
  }

  return (
    <div className='position-relative px-2'>
      <div className='position-absolute top-50 start-0 translate-middle ms-2'>{currentIcon}</div>
      <div className='ps-3 py-2' style={currentBorderStyle}>
        <div className='rounded-2 p-3' style={currentStepStyle}>
          <div
            className={classNames('mb-2 fs-12px', { 'text-secondary': !currentStepComplete && !currentStepCancelled })}
          >
            <span>{label}</span>
          </div>
          <span
            className={classNames('riforma-regular fs-14px', {
              'text-primary': !currentStepComplete && !currentStepCancelled
            })}
          >
            {description}
          </span>
        </div>
      </div>
    </div>
  );
};

export const StatusDetailsModal: React.FC<StatusDetailsModalProps> = ({ order, ...props }) => {
  let currentSequence = orderSequence;

  if (order.status === OrderStatus.CANCELLED) {
    const cancellationReason = order.cancellationReason || CancellationReason.OTHER;

    currentSequence = orderCancelledLabels[cancellationReason].sequence;
  } else if (order.invoice.paymentType === PaymentMethods.Wire) {
    currentSequence = wireOrderSequence;
  }

  const currentStatusIndex = currentSequence.indexOf(order.status);
  const maxStatusIndex = currentSequence.indexOf(OrderStatus.AWAITING_SHIPMENT);

  return (
    <Slideout {...props} title='Order details'>
      {currentSequence.map((status, index) => {
        if (maxStatusIndex !== -1 && index > maxStatusIndex) {
          return null;
        }

        const previousStepStatus = index - 1 < currentStatusIndex ? StepStatus.COMPLETE : StepStatus.INCOMPLETE;
        let currentStepStatus = index < currentStatusIndex ? StepStatus.COMPLETE : StepStatus.INCOMPLETE;

        if (status === OrderStatus.CANCELLED) {
          currentStepStatus = StepStatus.CANCELLED;
        }
        let nextStepStatus;

        if (index + 1 >= currentSequence.length) {
          nextStepStatus = StepStatus.COMPLETE;
        } else if (currentSequence[index + 1] === OrderStatus.CANCELLED) {
          nextStepStatus = StepStatus.CANCELLED;
        } else {
          nextStepStatus = index + 1 < currentStatusIndex ? StepStatus.COMPLETE : StepStatus.INCOMPLETE;
        }

        return (
          <StatusBlob
            key={`orderStatus-${index}`}
            order={order}
            renderedStatus={status}
            previousStepStatus={previousStepStatus}
            currentStepStatus={currentStepStatus}
            nextStepStatus={nextStepStatus}
          />
        );
      })}
      <div className='mb-6' />
    </Slideout>
  );
};
